import type { ReactNode } from "react";
import { formatTargetLabel } from "./TargetFields";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  announcementEligibility,
  announcementSeverityLabelFa,
  announcementSeverityTone,
  announcementStateLabelFa,
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
  type AnnouncementInventoryRow,
  type AnnouncementSortKey,
} from "../lib/announcements-ops";
import { formatDiffValue } from "../lib/content-state";
import { cmsStrings } from "../terminology";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  listFilter: string;
  onListFilterChange: (value: string) => void;
  queueCount: number;
  query: string;
  onQueryChange: (value: string) => void;
  sort: AnnouncementSortKey;
  onSortChange: (value: AnnouncementSortKey) => void;
};

export function AnnouncementsFilterBar({
  listFilter,
  onListFilterChange,
  queueCount,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: FilterBarProps) {
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label={cmsStrings.inventory.filterStatusAria}
    >
      <div className="filter-toolbar__chips announcements-filter-bar__chips" role="group" aria-label={cmsStrings.inventory.filterStatusAria}>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_ALL ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_ALL)}
          aria-pressed={listFilter === FILTER_ALL}
        >
          {cmsStrings.inventory.filterAll}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_QUEUE ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_QUEUE)}
          aria-pressed={listFilter === FILTER_QUEUE}
        >
          {cmsStrings.inventory.filterQueue}
          {queueCount > 0 && (
            <>
              {" "}
              (
              <LtrIsolate mono>{queueCount}</LtrIsolate>
              )
            </>
          )}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_PAUSED ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_PAUSED)}
          aria-pressed={listFilter === FILTER_PAUSED}
        >
          {cmsStrings.inventory.filterPaused}
        </button>
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">{cmsStrings.announcementsUi.searchAria}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={cmsStrings.announcementsUi.searchPlaceholder}
          aria-label={cmsStrings.announcementsUi.searchAria}
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>{cmsStrings.inventory.sortLabel}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as AnnouncementSortKey)}
          aria-label={cmsStrings.announcementsUi.sortAria}
        >
          <option value="updated">{cmsStrings.inventory.sortUpdated}</option>
          <option value="title">{cmsStrings.inventory.sortTitle}</option>
          <option value="severity">{cmsStrings.inventory.sortSeverity}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type InspectProps = {
  item: AnnouncementInventoryRow;
  actions?: ReactNode;
};

export function AnnouncementInspectSummary({ item, actions }: InspectProps) {
  const eligibility = announcementEligibility(item);
  const target = formatTargetLabel(item);
  const ui = cmsStrings.announcementsUi;
  const composer = cmsStrings.announcementsComposer;

  return (
    <section className="content-inspect" aria-labelledby="announcement-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <h2 id="announcement-inspect-title" className="content-inspect__name">
            {item.title}
          </h2>
          <p className="content-inspect__id">
            {cmsStrings.inventory.factId}{" "}
            <LtrIsolate mono>{item.id}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label={ui.statusAria}>
          <span className={`state-badge state-badge--${item.state}`}>
            {announcementStateLabelFa(item.state)}
          </span>
          <span
            className={`state-badge ${TONE_BADGE[announcementSeverityTone(item.severity)]}`}
          >
            {announcementSeverityLabelFa(item.severity)}
          </span>
          {item.emergency && (
            <span className="state-badge state-badge--emergency">{cmsStrings.inventory.emergencyBadge}</span>
          )}
        </div>
      </header>

      <dl className="content-inspect__facts">
        <div className="content-inspect__fact--wide">
          <dt>{composer.bodyLabel}</dt>
          <dd className="content-inspect__body">{item.body}</dd>
        </div>
        <div>
          <dt>{composer.dismissible}</dt>
          <dd>{formatDiffValue(item.dismissible)}</dd>
        </div>
        <div>
          <dt>{cmsStrings.inventory.factTargeting}</dt>
          <dd>
            {target}
            {item.siteCode && (
              <>
                {" "}
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  ({item.siteCode})
                </bdi>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>{cmsStrings.inventory.factSchedule}</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.startsAt)} – {formatKabulDisplay(item.endsAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>{cmsStrings.inventory.factPriority}</dt>
          <dd>
            <LtrIsolate mono>{item.priority}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>{ui.factEligibility}</dt>
          <dd>{eligibility.label}</dd>
        </div>
        {item.updatedAt && (
          <div>
            <dt>{cmsStrings.inventory.factUpdated}</dt>
            <dd>
              <LtrIsolate mono>{formatKabulDisplay(item.updatedAt)}</LtrIsolate>
            </dd>
          </div>
        )}
        {item.submittedAt && (
          <div>
            <dt>{ui.factSubmitted}</dt>
            <dd>
              <LtrIsolate mono>{formatKabulDisplay(item.submittedAt)}</LtrIsolate>
            </dd>
          </div>
        )}
        {item.emergency && item.emergencyAudience && (
          <div>
            <dt>{composer.emergencyAudience}</dt>
            <dd>
              {item.emergencyAudience === "platform"
                ? composer.emergencyPlatform
                : composer.emergencyOrgOnly}
            </dd>
          </div>
        )}
        {item.rejectionReason && (
          <div>
            <dt>{composer.rejectReason}</dt>
            <dd>{item.rejectionReason}</dd>
          </div>
        )}
        {item.state === "paused" && item.pausedFromState && (
          <div>
            <dt>{ui.factPausedFrom}</dt>
            <dd>{announcementStateLabelFa(item.pausedFromState)}</dd>
          </div>
        )}
      </dl>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

type ListProps = {
  rows: AnnouncementInventoryRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: AnnouncementInventoryRow) => ReactNode;
};

export function AnnouncementsInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: ListProps) {
  const ui = cmsStrings.announcementsUi;

  return (
    <>
      <div className="sites-inventory-table-wrap inventory-table-wrap" role="region" aria-label={ui.tableAria}>
        <table className="data-table content-inventory-table inventory-table announcements-inventory-table">
          <thead>
            <tr>
              <th scope="col">{cmsStrings.inventory.sortTitle}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colTarget}</th>
              <th scope="col">{cmsStrings.inventory.factSchedule}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                      aria-label={`${cmsStrings.inventory.details}: ${row.title}`}
                    >
                      {row.title}
                    </button>
                    <div className="sites-inventory__muted">
                      {announcementSeverityLabelFa(row.severity)}
                      {row.emergency ? ` · ${cmsStrings.inventory.emergencyBadge}` : ""}
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge state-badge--${row.state}`}>
                      {announcementStateLabelFa(row.state)}
                    </span>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={formatTargetLabel(row)}>
                      {formatTargetLabel(row)}
                    </div>
                    {row.siteCode && (
                      <div className="sites-inventory__muted inventory-cell-truncate" title={row.siteCode}>
                        <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={`${formatKabulDisplay(row.startsAt)} – ${formatKabulDisplay(row.endsAt)}`}>
                      <LtrIsolate mono>{formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}</LtrIsolate>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="sites-inventory-cards content-inventory-cards inventory-cards" aria-label={ui.cardsAria}>
        {rows.map((row) => {
          const selected = row.id === selectedId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
                aria-label={`${cmsStrings.inventory.details}: ${row.title}`}
              >
                <span className="sites-inventory-card__title">{row.title}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge state-badge--${row.state}`}>
                    {announcementStateLabelFa(row.state)}
                  </span>
                  {row.emergency && (
                    <span className="state-badge state-badge--emergency">{cmsStrings.inventory.emergencyBadge}</span>
                  )}
                </span>
                <span className="sites-inventory__muted">
                  {announcementSeverityLabelFa(row.severity)}
                  {" · "}
                  {ui.colTarget}: {formatTargetLabel(row)}
                </span>
                <span className="sites-inventory__muted">
                  {cmsStrings.inventory.factSchedule}:{" "}
                  <LtrIsolate mono>{formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}</LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
